import { Model } from 'pinia-orm';
import type {
  StepTypeCamelCase,
  StepTypeKebabCase,
} from 'src/types/course/step';
import type { StepType } from './StepType';
import { Str, Uid, HasMany, Attr, Num } from 'pinia-orm/decorators';
import { SectionStep } from 'modules/Section/models/SectionStep';
import type { ApiStepStep } from 'src/types/contentTypes';
import type { StrapiAttr } from 'src/types/strapi';
import { toPascalCase, toKebabCase } from 'src/shared/utilities/string';

/** Scalar field types are derived from the backend schema — see src/types/strapi.ts. */
type StepAttr<K extends keyof ApiStepStep['attributes']> = StrapiAttr<
  ApiStepStep,
  K
>;

export class Step extends Model {
  static override entity = 'steps';

  @Uid() declare id: number;
  @Str(null) declare documentId: string | null;
  @Str('') declare step_name: StepAttr<'step_name'>;
  @Str('') declare instructions: StepAttr<'instructions'>;
  @Str('') declare step_category: StepAttr<'step_category'>;
  @Str('') declare function: StepAttr<'function'>;
  // NOT derived from Strapi on purpose: the backend enum is spaced/title-case
  // ('Single Choice'), but the mutator below kebab-cases every value, so the
  // type this model actually holds is StepTypeKebabCase ('single-choice').
  @Str('') declare type: StepTypeKebabCase;
  @Num(null) declare user_step_id: number[];
  @Attr(null) declare step_type: Partial<StepType[]> | null;
  @Attr(null) declare tags: StepAttr<'tags'> | null;

  @HasMany(() => SectionStep, 'id') declare section_step_id:
    | SectionStep[]
    | null;

  get typePascalCase(): StepTypeCamelCase {
    return toPascalCase(this.type) as StepTypeCamelCase;
  }

  static override mutators() {
    return {
      type(value: string) {
        return toKebabCase(value.trim()) as StepTypeKebabCase;
      },
    };
  }
}
